/**
 * Delivery promise (BUILD-SPEC-v3 section 7). Pure functions, no DOM.
 * Loaded in the browser as window.BotaniqueDelivery and in Node by
 * scripts/test-delivery.mjs.
 *
 * Business days skip Saturday, Sunday and shipping holidays. An order placed on a
 * business day before the cutoff (America/New_York) counts that day as day 0.
 * Ship date = order day + processing days + product lead days (business days).
 * Arrival window = ship date + transit min .. ship date + transit max (business days).
 */
(function (root) {
  const TIME_ZONE = 'America/New_York';

  // First three ZIP digits for the 8 states we ship to.
  const ZIP_PREFIXES = {
    NJ: [[70, 89]],
    NY: [[5, 5], [100, 149]],
    PA: [[150, 196]],
    CT: [[60, 69]],
    MA: [[10, 27], [55, 55]],
    MD: [[206, 219]],
    DC: [[200, 200], [202, 205], [569, 569]],
    VA: [[201, 201], [220, 246]],
  };

  function stateForZip(zip) {
    const digits = String(zip || '').trim().match(/^(\d{5})(?:-\d{4})?$/);
    if (!digits) return null;
    const prefix = Number(digits[1].slice(0, 3));
    for (const [state, ranges] of Object.entries(ZIP_PREFIXES)) {
      if (ranges.some(([lo, hi]) => prefix >= lo && prefix <= hi)) return state;
    }
    return 'OTHER';
  }

  // Calendar date and hour in New York for a given instant.
  function nyParts(date) {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: TIME_ZONE,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hourCycle: 'h23',
    }).formatToParts(date);
    const get = (type) => Number(parts.find((p) => p.type === type).value);
    return { y: get('year'), m: get('month'), d: get('day'), hour: get('hour'), minute: get('minute') };
  }

  // Calendar dates are handled as UTC noon to stay clear of DST edges.
  const day = (y, m, d) => new Date(Date.UTC(y, m - 1, d, 12));
  const iso = (date) => date.toISOString().slice(0, 10);
  const addDays = (date, n) => new Date(date.getTime() + n * 86400000);

  function isBusinessDay(date, holidays) {
    const dow = date.getUTCDay();
    return dow !== 0 && dow !== 6 && !holidays.has(iso(date));
  }

  function nextBusinessDay(date, holidays) {
    let d = date;
    while (!isBusinessDay(d, holidays)) d = addDays(d, 1);
    return d;
  }

  function addBusinessDays(date, n, holidays) {
    let d = date;
    let left = n;
    while (left > 0) {
      d = addDays(d, 1);
      if (isBusinessDay(d, holidays)) left -= 1;
    }
    return d;
  }

  /**
   * @param {object} o
   * @param {Date} o.now
   * @param {number} o.cutoffHour      24h hour in New York, e.g. 12
   * @param {number} o.processingDays  business days before shipping
   * @param {number} [o.leadDays]      extra business days for this product (specs.lead_time_days)
   * @param {{min:number,max:number}} o.transit  business days in transit for the state
   * @param {string[]} [o.holidays]    ISO dates
   * @returns {{orderDay:string, cutoffToday:boolean, shipDate:string, earliest:string, latest:string}}
   */
  function estimate(o) {
    const holidays = new Set(o.holidays || []);
    const p = nyParts(o.now);
    const today = day(p.y, p.m, p.d);
    const beforeCutoff = p.hour < o.cutoffHour;
    const cutoffToday = beforeCutoff && isBusinessDay(today, holidays);
    const orderDay = cutoffToday ? today : nextBusinessDay(addDays(today, 1), holidays);
    const ship = addBusinessDays(orderDay, (o.processingDays || 0) + (o.leadDays || 0), holidays);
    const earliest = addBusinessDays(ship, o.transit.min, holidays);
    const latest = addBusinessDays(ship, Math.max(o.transit.max, o.transit.min), holidays);
    return { orderDay: iso(orderDay), cutoffToday, shipDate: iso(ship), earliest: iso(earliest), latest: iso(latest) };
  }

  // "Thu, Oct 30" from an ISO date.
  function formatDay(isoDate, locale) {
    const [y, m, d] = isoDate.split('-').map(Number);
    return new Intl.DateTimeFormat(locale || 'en-US', { weekday: 'short', month: 'short', day: 'numeric', timeZone: 'UTC' }).format(
      day(y, m, d),
    );
  }

  // "12:00 pm" from an hour.
  function formatHour(hour) {
    const h = hour % 12 === 0 ? 12 : hour % 12;
    return `${h}:00 ${hour < 12 ? 'am' : 'pm'}`;
  }

  // Business days from a to b (both ISO), counting b. Used for the event date check.
  function businessDaysBetween(a, b, holidaysList) {
    const holidays = new Set(holidaysList || []);
    const [ya, ma, da] = a.split('-').map(Number);
    const [yb, mb, db] = b.split('-').map(Number);
    let d = day(ya, ma, da);
    const end = day(yb, mb, db);
    if (end <= d) return -Math.round((d - end) / 86400000);
    let n = 0;
    while (d < end) {
      d = addDays(d, 1);
      if (isBusinessDay(d, holidays)) n += 1;
    }
    return n;
  }

  const api = { stateForZip, estimate, formatDay, formatHour, businessDaysBetween, isBusinessDay, ZIP_PREFIXES };
  root.BotaniqueDelivery = api;
})(typeof window !== 'undefined' ? window : globalThis);
